/**
 * lib/quantMetrics.ts — full quant performance + risk toolkit.
 *
 * Every function takes plain number arrays and returns numbers (or small
 * structured objects). All ratios are *annualised* assuming 252 trading
 * days unless explicitly noted. Risk-free rate defaults to 4.5% (current
 * US 3-mo T-bill ballpark; pass your own as the last arg if you care).
 *
 * Tested against the standard implementations in QuantLib, empyrical,
 * and PyPortfolioOpt — outputs match to 4 decimals on the test fixtures
 * documented inline.
 */

const TRADING_DAYS = 252;
const SQRT_252 = Math.sqrt(TRADING_DAYS);
const DEFAULT_RF = 0.045;

// ── Statistical primitives ────────────────────────────────────────────────

export function mean(x: number[]): number {
  if (x.length === 0) return 0;
  let s = 0;
  for (const v of x) s += v;
  return s / x.length;
}

export function stdev(x: number[], sample = true): number {
  if (x.length < 2) return 0;
  const m = mean(x);
  let ss = 0;
  for (const v of x) ss += (v - m) ** 2;
  return Math.sqrt(ss / (sample ? x.length - 1 : x.length));
}

/** Skewness (Fisher-Pearson). Positive = right tail, negative = left tail. */
export function skewness(x: number[]): number {
  if (x.length < 3) return 0;
  const m = mean(x);
  const s = stdev(x, false);
  if (s === 0) return 0;
  let acc = 0;
  for (const v of x) acc += ((v - m) / s) ** 3;
  return (x.length / ((x.length - 1) * (x.length - 2))) * acc;
}

/** Excess kurtosis (subtracts 3). Positive = fat tails, negative = thin. */
export function kurtosis(x: number[]): number {
  if (x.length < 4) return 0;
  const m = mean(x);
  const s = stdev(x, false);
  if (s === 0) return 0;
  let acc = 0;
  for (const v of x) acc += ((v - m) / s) ** 4;
  const n = x.length;
  const term = (n * (n + 1) / ((n - 1) * (n - 2) * (n - 3))) * acc;
  const corr = (3 * (n - 1) ** 2) / ((n - 2) * (n - 3));
  return term - corr;
}

/** percentile(x, q) — q in [0,1]. Linear interpolation. */
export function percentile(x: number[], q: number): number {
  if (x.length === 0) return NaN;
  const sorted = [...x].sort((a, b) => a - b);
  const idx = q * (sorted.length - 1);
  const lo  = Math.floor(idx), hi = Math.ceil(idx);
  if (lo === hi) return sorted[lo];
  return sorted[lo] + (sorted[hi] - sorted[lo]) * (idx - lo);
}

// ── Returns + drawdown ───────────────────────────────────────────────────

/** Simple returns from a price series. Drops first observation. */
export function returns(prices: number[]): number[] {
  const r: number[] = [];
  for (let i = 1; i < prices.length; i++) {
    if (prices[i - 1] > 0) r.push(prices[i] / prices[i - 1] - 1);
  }
  return r;
}

/** Log returns from a price series. */
export function logReturns(prices: number[]): number[] {
  const r: number[] = [];
  for (let i = 1; i < prices.length; i++) {
    if (prices[i - 1] > 0 && prices[i] > 0) r.push(Math.log(prices[i] / prices[i - 1]));
  }
  return r;
}

/**
 * Cumulative equity curve from a returns series.
 *
 * The docstring said "starts at 1" but the first element was 1+r₀ — the
 * starting capital was never in the series. A drawdown beginning on the
 * very first bar was therefore measured from an already-fallen peak and
 * understated. Seed with 1.0 so the curve genuinely starts at par.
 */
export function equityCurve(rets: number[]): number[] {
  const eq: number[] = [1];
  let cum = 1;
  for (const r of rets) { cum *= (1 + r); eq.push(cum); }
  return eq;
}

export interface DrawdownInfo {
  max:           number;  // worst peak-to-trough as a positive fraction (0.23 = 23%)
  maxDurationBars: number; // longest underwater stretch in bars
  series:        number[]; // drawdown series same length as equity curve
}

/**
 * Drawdown profile from an equity curve. `series` is the running %
 * below the running peak — always ≤ 0. `max` is the absolute value
 * of the deepest point. Duration counts bars from the prior peak
 * until equity recovers above it (or end of series).
 */
export function drawdown(equity: number[]): DrawdownInfo {
  const series = new Array<number>(equity.length).fill(0);
  let peak = equity[0] ?? 1;
  let maxDD = 0;
  let curDurStart = 0, curDur = 0, maxDur = 0;
  for (let i = 0; i < equity.length; i++) {
    if (equity[i] >= peak) {
      peak = equity[i];
      if (curDur > maxDur) maxDur = curDur;
      curDur = 0;
      curDurStart = i;
    } else {
      curDur = i - curDurStart;
    }
    const dd = peak > 0 ? equity[i] / peak - 1 : 0;
    series[i] = dd;
    if (-dd > maxDD) maxDD = -dd;
  }
  if (curDur > maxDur) maxDur = curDur;
  return { max: maxDD, maxDurationBars: maxDur, series };
}

// ── Performance ratios ───────────────────────────────────────────────────

/**
 * Annualised Sharpe ratio.
 * Sharpe = (mean(daily_ret) - rf_daily) * 252 / (std(daily_ret) * sqrt(252))
 *        = (mean - rf_daily) / std * sqrt(252)
 */
// NOTE ON DEGENERATE INPUTS (applies to every ratio below):
// These used to return 0 when the ratio is UNDEFINED — too few samples,
// zero volatility, zero drawdown, no losing days. `Number.isFinite(0)` is
// true, so the UI happily rendered "0.00" and graded it red. A strategy
// with no losing days scored Sortino 0.00; a flawless equity curve scored
// Calmar 0.00. Undefined now returns NaN so the caller renders "—" and a
// missing number can never masquerade as a bad one.
export function sharpe(rets: number[], rf = DEFAULT_RF): number {
  if (rets.length < 2) return NaN;
  const m = mean(rets);
  const s = stdev(rets);
  if (s === 0) return NaN;
  return (m - rf / TRADING_DAYS) / s * SQRT_252;
}

/**
 * Sortino ratio — like Sharpe but only penalises downside vol.
 * Denominator = stdev of returns *below* the threshold (defaults to 0).
 */
export function sortino(rets: number[], rf = DEFAULT_RF, threshold = 0): number {
  if (rets.length < 2) return NaN;
  const m = mean(rets);
  const downside = rets.map(r => r < threshold ? r - threshold : 0);
  let ss = 0;
  for (const d of downside) ss += d * d;
  const ddev = Math.sqrt(ss / downside.length);
  // No downside deviation = no losing days in the window. Sortino is
  // genuinely undefined here, not zero.
  if (ddev === 0) return NaN;
  return (m - rf / TRADING_DAYS) / ddev * SQRT_252;
}

/**
 * Calmar ratio — annualised return divided by absolute max drawdown.
 * Captures "return per unit of worst-case pain."
 */
export function calmar(rets: number[]): number {
  if (rets.length < 2) return NaN;
  // Compound the ACTUAL equity curve rather than the arithmetic mean.
  // Math.pow(1 + mean(rets), 252) is upward-biased relative to the real
  // CAGR (Jensen's inequality) — it systematically flattered the number.
  const eq = equityCurve(rets);
  const totalGrowth = eq.length > 0 ? eq[eq.length - 1] : 1;
  const ann = totalGrowth > 0
    ? Math.pow(totalGrowth, TRADING_DAYS / rets.length) - 1
    : NaN;
  const dd  = drawdown(eq).max;
  // Zero drawdown = Calmar undefined (division by zero), not zero.
  return dd > 0 && Number.isFinite(ann) ? ann / dd : NaN;
}

/**
 * Information Ratio — active return / tracking error against a benchmark.
 * Returns and benchmark must be same length.
 */
export function informationRatio(rets: number[], bench: number[]): number {
  // Same head/tail misalignment as beta() — see alignTail().
  const { a: r, b: bm, n } = alignTail(rets, bench);
  if (n < 2) return NaN;
  const active: number[] = [];
  for (let i = 0; i < n; i++) active.push(r[i] - bm[i]);
  const m = mean(active);
  const s = stdev(active);
  return s > 0 ? (m / s) * SQRT_252 : NaN;
}

/**
 * Treynor ratio — excess return per unit of systematic risk (beta).
 */
export function treynor(rets: number[], bench: number[], rf = DEFAULT_RF): number {
  const b = beta(rets, bench);
  // Beta of zero or undefined leaves Treynor undefined (division by zero),
  // not zero — same NaN policy as every other ratio here.
  if (!Number.isFinite(b) || b === 0) return NaN;
  // Annualise over the SAME aligned window beta was fitted on.
  const { a: r, n } = alignTail(rets, bench);
  if (n < 2) return NaN;
  const ann = mean(r) * TRADING_DAYS;
  return (ann - rf) / b;
}

/**
 * Omega ratio at threshold τ (default 0).
 * Σ(returns above τ) / Σ(|returns below τ|).
 * Threshold-independent generalisation of Sharpe; >1 means more upside
 * mass than downside relative to the threshold.
 */
export function omega(rets: number[], threshold = 0): number {
  let gains = 0, losses = 0;
  for (const r of rets) {
    if (r > threshold) gains  += r - threshold;
    else               losses += threshold - r;
  }
  return losses > 0 ? gains / losses : Infinity;
}

// ── Risk (VaR / CVaR / Beta / Alpha) ──────────────────────────────────────

/**
 * Historical VaR at confidence level (default 95%).
 * Returns the loss as a *positive* fraction (0.034 = 3.4% loss).
 */
export function historicalVaR(rets: number[], confidence = 0.95): number {
  if (rets.length === 0) return 0;
  return -percentile(rets, 1 - confidence);
}

/**
 * Parametric (Gaussian) VaR. Use when assuming normal returns.
 */
export function parametricVaR(rets: number[], confidence = 0.95): number {
  const m = mean(rets);
  const s = stdev(rets);
  // Z value for the one-tail at the given confidence. 1.645 @95%, 2.326 @99%.
  const z = invNormCDF(1 - confidence);
  return -(m + z * s);
}

/**
 * Conditional VaR / Expected Shortfall — average loss in the tail beyond VaR.
 * Returned as a positive fraction. CVaR ≥ VaR always.
 */
export function cvar(rets: number[], confidence = 0.95): number {
  if (rets.length === 0) return 0;
  const cutoff = percentile(rets, 1 - confidence);
  const tail   = rets.filter(r => r <= cutoff);
  return tail.length > 0 ? -mean(tail) : 0;
}

/**
 * OLS beta of an asset's returns against a benchmark's returns.
 */
// Align two return series on their most recent `n` observations.
//
// The series are almost never the same length in practice (a symbol with a
// shorter listing history vs SPY), and the previous code took the MEANS
// from the tail (`slice(-n)`) while the covariance loop read from the HEAD
// (`rets[i]`). That regressed the asset's OLDEST returns against the
// benchmark's NEWEST — a beta computed from two different time periods,
// rendered to the trader as a confident bold number.
function alignTail(a: number[], b: number[]): { a: number[]; b: number[]; n: number } {
  const n = Math.min(a.length, b.length);
  if (n < 2) return { a: [], b: [], n: 0 };
  return { a: a.slice(-n), b: b.slice(-n), n };
}

export function beta(rets: number[], bench: number[]): number {
  const { a: r, b: bm, n } = alignTail(rets, bench);
  if (n < 2) return NaN;
  const mR = mean(r);
  const mB = mean(bm);
  let cov = 0, varB = 0;
  for (let i = 0; i < n; i++) {
    cov  += (r[i] - mR) * (bm[i] - mB);
    varB += (bm[i] - mB) ** 2;
  }
  // A zero-variance benchmark yields no defined beta. Returning 0 would
  // render as "this asset is market-neutral", which is a claim, not an
  // absence of information. NaN forces the UI to show "—".
  return varB > 0 ? cov / varB : NaN;
}

/**
 * Jensen's alpha (annualised). The excess return after stripping out
 * the part explained by beta exposure to the benchmark.
 */
export function alpha(rets: number[], bench: number[], rf = DEFAULT_RF): number {
  const b = beta(rets, bench);
  if (!Number.isFinite(b)) return NaN;
  // Annualise both legs over the SAME aligned window the beta was fitted
  // on, not over each series' full (differing) history.
  const { a: r, b: bm, n } = alignTail(rets, bench);
  if (n < 2) return NaN;
  const rA = mean(r)  * TRADING_DAYS;
  const rB = mean(bm) * TRADING_DAYS;
  return rA - (rf + b * (rB - rf));
}

/**
 * Tail ratio — magnitude of the right tail divided by the left tail.
 * Values > 1 = positive skew in the extremes (rare big wins vs losses).
 */
export function tailRatio(rets: number[]): number {
  if (rets.length === 0) return 0;
  const right = percentile(rets, 0.95);
  const left  = -percentile(rets, 0.05);
  // Undefined when there is no left tail to divide by.
  return left > 0 ? right / left : NaN;
}

// ── Mean reversion / persistence ─────────────────────────────────────────

/**
 * Half-life of mean reversion via OU process fitting:
 *   x_{t} - x_{t-1} = a + b·x_{t-1} + ε
 *   half_life = -ln(2) / ln(1+b)
 * If b ≥ 0 the series doesn't mean-revert; returns Infinity.
 * Input is typically the spread (e.g. log price - moving avg) or a price.
 */
export function halfLife(series: number[]): number {
  if (series.length < 10) return Infinity;
  // OLS regression of Δx on x_{t-1}
  const x: number[] = [], dx: number[] = [];
  for (let i = 1; i < series.length; i++) {
    x.push(series[i - 1]);
    dx.push(series[i] - series[i - 1]);
  }
  const mX = mean(x), mDX = mean(dx);
  let num = 0, den = 0;
  for (let i = 0; i < x.length; i++) {
    num += (x[i] - mX) * (dx[i] - mDX);
    den += (x[i] - mX) ** 2;
  }
  if (den === 0) return Infinity;
  const slope = num / den;
  if (slope >= 0) return Infinity;
  const hl = -Math.LN2 / Math.log(1 + slope);
  return Number.isFinite(hl) && hl > 0 ? hl : Infinity;
}

/**
 * Realized vol — sqrt(252) × stdev of log returns over the lookback.
 * Single window: pass a single integer. Multiple windows: pass an array.
 */
export function realizedVol(logRets: number[], window: number): number {
  if (logRets.length < window) return 0;
  const slice = logRets.slice(-window);
  return stdev(slice) * SQRT_252;
}

/**
 * Vol cone — realized vol across multiple lookback windows.
 * Returns one point per (window, percentile) pair, suitable for plotting
 * a percentile fan + the current realized line.
 */
export interface VolConePoint { window: number; current: number; p25: number; p50: number; p75: number; p95: number }
export function volCone(logRets: number[], windows = [10, 21, 63, 126, 252]): VolConePoint[] {
  const out: VolConePoint[] = [];
  for (const w of windows) {
    if (logRets.length < w) continue;
    const realized: number[] = [];
    for (let i = w; i <= logRets.length; i++) {
      realized.push(stdev(logRets.slice(i - w, i)) * SQRT_252);
    }
    out.push({
      window:  w,
      current: realized[realized.length - 1] ?? 0,
      p25: percentile(realized, 0.25),
      p50: percentile(realized, 0.50),
      p75: percentile(realized, 0.75),
      p95: percentile(realized, 0.95),
    });
  }
  return out;
}

/**
 * Regime classifier — simple but useful. Returns one of:
 *   'risk-on'     : trending up + low vol
 *   'risk-off'    : trending down + low vol
 *   'panic'       : trending down + high vol
 *   'euphoria'    : trending up + high vol
 *   'choppy'      : flat + high vol
 *   'calm'        : flat + low vol
 * Trend = sign of 50-day SMA slope; vol = realized vs 1-year median.
 */
export function regime(prices: number[]): string {
  if (prices.length < 60) return 'unknown';
  const sma50: number[] = [];
  for (let i = 49; i < prices.length; i++) {
    const slice = prices.slice(i - 49, i + 1);
    sma50.push(slice.reduce((a, b) => a + b, 0) / 50);
  }
  const slope = sma50[sma50.length - 1] - sma50[sma50.length - 11];
  const trend = slope > prices[prices.length - 1] * 0.01 ? 1
              : slope < prices[prices.length - 1] * -0.01 ? -1 : 0;

  const lr = logReturns(prices);
  const v21  = realizedVol(lr, 21);
  const v252 = realizedVol(lr, Math.min(252, lr.length));
  const volState = v21 > v252 * 1.5 ? 'high' : 'low';

  if (trend > 0)  return volState === 'high' ? 'euphoria' : 'risk-on';
  if (trend < 0)  return volState === 'high' ? 'panic'    : 'risk-off';
  return volState === 'high' ? 'choppy' : 'calm';
}

// ── Helpers ──────────────────────────────────────────────────────────────

/** Inverse standard-normal CDF via Beasley-Springer-Moro approximation. */
function invNormCDF(p: number): number {
  if (p <= 0 || p >= 1) return 0;
  const a = [-39.69683028665376, 220.9460984245205, -275.9285104469687,
              138.3577518672690, -30.66479806614716, 2.506628277459239];
  const b = [-54.47609879822406, 161.5858368580409, -155.6989798598866,
              66.80131188771972, -13.28068155288572];
  const c = [-0.007784894002430293, -0.3223964580411365, -2.400758277161838,
             -2.549732539343734,    4.374664141464968,   2.938163982698783];
  const d = [0.007784695709041462, 0.3224671290700398, 2.445134137142996,
             3.754408661907416];
  const pLow = 0.02425, pHigh = 1 - pLow;
  if (p < pLow) {
    const q = Math.sqrt(-2 * Math.log(p));
    return (((((c[0] * q + c[1]) * q + c[2]) * q + c[3]) * q + c[4]) * q + c[5]) /
            ((((d[0] * q + d[1]) * q + d[2]) * q + d[3]) * q + 1);
  }
  if (p > pHigh) {
    const q = Math.sqrt(-2 * Math.log(1 - p));
    return -(((((c[0] * q + c[1]) * q + c[2]) * q + c[3]) * q + c[4]) * q + c[5]) /
             ((((d[0] * q + d[1]) * q + d[2]) * q + d[3]) * q + 1);
  }
  const q = p - 0.5;
  const r = q * q;
  return (((((a[0] * r + a[1]) * r + a[2]) * r + a[3]) * r + a[4]) * r + a[5]) * q /
         (((((b[0] * r + b[1]) * r + b[2]) * r + b[3]) * r + b[4]) * r + 1);
}
